import React, { useEffect, useMemo, useState } from "react";

function InfoIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="currentColor">
      <path d="M256 56C145.72 56 56 145.72 56 256s89.72 200 200 200 200-89.72 200-200S366.28 56 256 56zm0 82a26 26 0 11-26 26 26 26 0 0126-26zm48 226h-88a16 16 0 010-32h28v-88h-16a16 16 0 010-32h32a16 16 0 0116 16v104h28a16 16 0 010 32z" />
    </svg>
  );
}

function Tooltip({ text }) {
  if (!text) {
    return null;
  }

  return (
    <div className="tooltip-toggle-radio">
      <button type="button" className="tooltip-toggle p-0 border-0 bg-transparent">
        <InfoIcon />
      </button>
      <div className="tooltip">
        <div className="tooltip-inner">{text}</div>
      </div>
    </div>
  );
}

function updateHistory(queryString) {
  const nextUrl = queryString
    ? `${window.location.protocol}//${window.location.host}${window.location.pathname}${queryString}`
    : `${window.location.protocol}//${window.location.host}${window.location.pathname}`;

  window.history.pushState({ path: nextUrl }, "", nextUrl);
}

function isOtherQuerySelected(search) {
  const searchParams = new URLSearchParams(search);
  return searchParams.get("query-type") === "other";
}

export default function EnquirySelectorIsland({ config }) {
  const [selectedJourney, setSelectedJourney] = useState("");
  const [selectedOtherQueryUrl, setSelectedOtherQueryUrl] = useState("");
  const [showAllQueries, setShowAllQueries] = useState(config.initialQueryType === "other");

  const otherQueries = useMemo(() => config.otherQueries || [], [config.otherQueries]);

  useEffect(() => {
    if (config.initialQueryType === "other") {
      setSelectedJourney("other");
    }
  }, [config.initialQueryType]);

  useEffect(() => {
    const handlePopState = () => {
      const shouldShowAllQueries = isOtherQuerySelected(window.location.search);

      if (shouldShowAllQueries) {
        setShowAllQueries(true);
        setSelectedJourney("other");
        return;
      }

      setShowAllQueries(false);
      setSelectedJourney("");
      setSelectedOtherQueryUrl("");
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const firstStepDisabled = selectedJourney.length === 0;
  const secondStepDisabled = selectedOtherQueryUrl.length === 0;

  const handleNext = () => {
    if (!showAllQueries) {
      if (selectedJourney === "technical") {
        window.location.href = config.technicalNextUrl;
        return;
      }

      if (selectedJourney === "other") {
        setShowAllQueries(true);
        updateHistory("?query-type=other");
      }

      return;
    }

    if (selectedOtherQueryUrl) {
      window.location.href = selectedOtherQueryUrl;
    }
  };

  const handleBack = () => {
    setShowAllQueries(false);
    setSelectedJourney("");
    setSelectedOtherQueryUrl("");
    updateHistory("");
  };

  return (
    <div id="enquirySelector">
      {!showAllQueries ? (
        <section className="enquirySelector">
          <div className="row clearfix">
            <div className="col-md-8 offset-md-2 text-center my-4 text-darkBlue">
              <h1 className="text-bolder text-dark-blue">{config.labels.reasonOfQueryTitle}</h1>
            </div>
          </div>
          <div className="row clearfix">
            <div className="col-md-6 column mb-3">
              <div className="btn-selector-box d-flex flex-column">
                <div className="d-flex flex-nowrap align-items-center position-relative">
                  <input
                    type="radio"
                    name="enquirySelector"
                    id="technical"
                    value="technical"
                    checked={selectedJourney === "technical"}
                    onChange={(event) => setSelectedJourney(event.target.value)}
                    className="d-none"
                    autoComplete="off"
                  />
                  <label htmlFor="technical" className="radio-lable">
                    <span className="radio-title">{config.labels.technicalQueryTitle}</span>
                  </label>
                  <Tooltip text={config.labels.technicalQueryTitleHint} />
                </div>
                <span className="radio-subTitle">{config.labels.technicalQuerySubTitle}</span>
              </div>
            </div>

            <div className="col-md-6 column mb-3">
              <div className="btn-selector-box d-flex flex-column">
                <div className="d-flex flex-nowrap position-relative">
                  <input
                    type="radio"
                    name="enquirySelector"
                    id="all"
                    value="other"
                    checked={selectedJourney === "other"}
                    onChange={(event) => setSelectedJourney(event.target.value)}
                    className="d-none"
                    autoComplete="off"
                  />
                  <label htmlFor="all" className="radio-lable">
                    <span className="radio-title">{config.labels.allQueryTitle}</span>
                  </label>
                  <Tooltip text={config.labels.allQueryTitleHint} />
                </div>
                <span className="radio-subTitle">{config.labels.allQuerySubTitle}</span>
              </div>
            </div>
          </div>
          <div className="row clearfix">
            <div className="col-6 col-md-3 offset-md-3 column mb-3">
              <a href={config.backUrl} className="btn btn-wide btn-arrow-left btn-back">
                <span>{config.labels.backLabel}</span>
              </a>
            </div>
            <div className="col-6 col-md-3 column mb-3">
              <button
                type="button"
                role="button"
                className={`btn btn-wide btn-arrow-right btn-next${firstStepDisabled ? " disabled" : ""}`}
                disabled={firstStepDisabled}
                onClick={handleNext}
              >
                <span>{config.labels.nextLabel}</span>
              </button>
            </div>
          </div>
        </section>
      ) : (
        <section id="allQueries">
          <div className="row clearfix">
            <div className="col-md-8 offset-md-2 text-center my-4 text-darkBlue">
              <h1 className="text-bolder text-dark-blue">{config.labels.allQueryPageTitle}</h1>
            </div>
          </div>
          <div className="bg-lightest-blue py-5 mb-5 rounded-xl">
            <div className="row clearfix">
              <div className="col-md-8 offset-md-2">
                <ul className="list-radio row">
                  {otherQueries.map((item, index) => (
                    <li key={item.id || index} className="col-md-6 radio-style-button">
                      <input
                        type="radio"
                        name="allQueries"
                        id={`all_${index}`}
                        className="d-none"
                        value={item.nextUrl}
                        checked={selectedOtherQueryUrl === item.nextUrl}
                        onChange={(event) => setSelectedOtherQueryUrl(event.target.value)}
                        autoComplete="off"
                      />
                      <label htmlFor={`all_${index}`}>
                        <span className="radio-cover">
                          <span className="label">{item.title}</span>
                          {item.subtitle ? <i className="my-2">{item.subtitle}</i> : null}
                          {item.systemMaintenanceAndOutageHtml ? (
                            <span
                              className="note"
                              dangerouslySetInnerHTML={{ __html: item.systemMaintenanceAndOutageHtml }}
                            />
                          ) : null}
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
          <div className="row clearfix">
            <div className="col-6 col-md-3 offset-md-3 column mb-3">
              <button type="button" role="button" className="btn btn-wide btn-arrow-left btn-back" onClick={handleBack}>
                <span>{config.labels.backLabel}</span>
              </button>
            </div>
            <div className="col-6 col-md-3 column mb-3">
              <button
                type="button"
                role="button"
                className={`btn btn-wide btn-arrow-right btn-next${secondStepDisabled ? " disabled" : ""}`}
                disabled={secondStepDisabled}
                onClick={handleNext}
              >
                <span>{config.labels.nextLabel}</span>
              </button>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
